"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { getCompanyName } from "@/lib/textron-data-processor";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";

interface PPENonCurrentAssetsHeldForSaleProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

type YesNo = "yes" | "no" | "";

type AssetRow = {
  id: string;
  category: string;
  name: string;
  relationship: "related-party" | "not-applicable" | "";
  remarks: string;
};

type CarryingComponentRow = {
  id: string;
  component: string;
  carryingAmount: string;
};

const FILE_NAME = "Execution_PPE_NCAHS.json";

interface NCAHSPersistedPayload {
  version: number;
  lastUpdated?: string;
  companyName?: string;
  hasAssetsHeldForSale?: YesNo;
  assets?: AssetRow[];
  valuedAtLower?: YesNo;
  hasDiscontinuedOperation?: YesNo;
  conditionAnswers?: Record<string, Record<string, YesNo>>;
  carryingRowsByAsset?: Record<string, CarryingComponentRow[]>;
  fairValueByAsset?: Record<string, string>;
  costToSellByAsset?: Record<string, string>;
}

const RELATIONSHIP_OPTIONS = [
  { value: "related-party", label: "Related Party" },
  { value: "not-applicable", label: "Not Applicable" },
];

const CONDITIONS = [
  {
    reference: "105:06",
    text: "An entity shall classify a non-current asset (or disposal group) as held for sale if its carrying amount will be recovered principally through a sale transaction rather than through continuing use.",
  },
  {
    reference: "105:07",
    text: "The asset (or disposal group) must be available for immediate sale in its present condition subject only to terms that are usual and customary for sales of such assets (or disposal groups).",
  },
  {
    reference: "105:07",
    text: "The sale of the asset (or disposal group) must be highly probable.",
  },
  {
    reference: "",
    text: "For the sale to be highly probable:",
  },
  {
    reference: "105:08",
    text: "- The appropriate level of management must be committed to a plan to sell the asset (or disposal group).",
  },
  {
    reference: "105:08",
    text: "- An active programme to locate a buyer must exist and complete the plan must have been initiated.",
  },
  {
    reference: "105:08",
    text: "- The asset (or disposal group) must be actively marketed for sale at a price that is reasonable in relation to its current fair value.",
  },
  {
    reference: "105:08",
    text: "The sale should be expected to qualify for recognition as a completed sale within one year from the date of classification, except as permitted by paragraph 9.",
  },
  {
    reference: "105:09",
    text: "Events or circumstances may extend the period to complete the sale beyond one year. An extension of the period required to complete a sale does not preclude an asset (or disposal group) from being classified as held for sale if the delay is caused by events or circumstances beyond the entity's control and there is sufficient evidence that the entity remains committed to its plan to sell the asset (or disposal group).",
  },
];

const DEFAULT_EXECUTION_PHASES = [
  "Employee Benefits Expense",
  "Property, Plant & Equipment",
  "Intangible Assets",
  "Lease Liability",
  "Right-of-Use Assets",
  "Cost of Materials Consumed",
  "Trade Payables",
  "Inventory",
  "Other Assets",
  "Other Financial Assets",
  "Trade Receivables",
  "Depreciation",
  "Impairment Testing",
  "Other Expenses",
  "Borrowings",
  "Cash & Cash Equivalents",
  "Finance Cost",
  "Income Tax",
  "Investments",
  "Other Income",
  "Other Liabilities",
  "Other Receivables",
  "Provisions & Contingent Liabilities",
  "Going Concern",
  "Litigations & Claims",
  "Opening Balance Testing",
  "Related Party Disclosure",
  "Equity",
  "Management Use of Expert",
  "Use of Auditor's Specialist",
  "Compliance with Laws and Regulations",
  "Review of Estimates for Management Bias",
  "Internal Audit",
  "Financial Closing & Reporting Process",
  "Management Override of Controls",
  "Biological Assets other than Bearer Plants",
];

function newAssetRow(): AssetRow {
  return {
    id: crypto.randomUUID(),
    category: "Non-current assets / Disposal Group",
    name: "",
    relationship: "",
    remarks: "",
  };
}

function newCarryingComponentRow(): CarryingComponentRow {
  return {
    id: crypto.randomUUID(),
    component: "",
    carryingAmount: "",
  };
}

function isYesNo(v: unknown): v is YesNo {
  return v === "yes" || v === "no" || v === "";
}

function normalizeRelationship(v: unknown): AssetRow["relationship"] {
  if (v === "related-party" || v === "not-applicable" || v === "") return v;
  return "";
}

function parseAssetRow(raw: unknown): AssetRow | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === "string" && o.id ? o.id : crypto.randomUUID();
  const category =
    typeof o.category === "string"
      ? o.category
      : "Non-current assets / Disposal Group";
  const name = typeof o.name === "string" ? o.name : "";
  const remarks = typeof o.remarks === "string" ? o.remarks : "";
  return {
    id,
    category,
    name,
    relationship: normalizeRelationship(o.relationship),
    remarks,
  };
}

function parseCarryingRow(raw: unknown): CarryingComponentRow | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  return {
    id: typeof o.id === "string" && o.id ? o.id : crypto.randomUUID(),
    component: typeof o.component === "string" ? o.component : "",
    carryingAmount: typeof o.carryingAmount === "string" ? o.carryingAmount : "",
  };
}

export default function PPENonCurrentAssetsHeldForSale({
  onBack,
  setActiveSection,
}: PPENonCurrentAssetsHeldForSaleProps) {
  const { isArchivalMode } = useArchivalMode();
  const [hasAssetsHeldForSale, setHasAssetsHeldForSale] = useState<YesNo>("");
  const [assets, setAssets] = useState<AssetRow[]>([newAssetRow()]);
  const [valuedAtLower, setValuedAtLower] = useState<YesNo>("");
  const [hasDiscontinuedOperation, setHasDiscontinuedOperation] =
    useState<YesNo>("");

  const [conditionAnswers, setConditionAnswers] = useState<
    Record<string, Record<number, YesNo>>
  >({});
  const [carryingRowsByAsset, setCarryingRowsByAsset] = useState<
    Record<string, CarryingComponentRow[]>
  >({});
  const [fairValueByAsset, setFairValueByAsset] = useState<Record<string, string>>(
    {},
  );
  const [costToSellByAsset, setCostToSellByAsset] = useState<
    Record<string, string>
  >({});

  const [companyName, setCompanyName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  const executionPhases = DEFAULT_EXECUTION_PHASES;

  useEffect(() => {
    const loadCompany = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch {
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompany();
  }, []);

  useEffect(() => {
    const loadFile = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(
          FILE_NAME,
        );
        if (result?.success && result?.data && typeof result.data === "object") {
          const data = result.data as NCAHSPersistedPayload;
          if (isYesNo(data.hasAssetsHeldForSale)) {
            setHasAssetsHeldForSale(data.hasAssetsHeldForSale);
          }
          if (Array.isArray(data.assets) && data.assets.length > 0) {
            const rows = data.assets.map(parseAssetRow).filter(Boolean) as AssetRow[];
            setAssets(rows.length > 0 ? rows : [newAssetRow()]);
          }
          if (isYesNo(data.valuedAtLower)) setValuedAtLower(data.valuedAtLower);
          if (isYesNo(data.hasDiscontinuedOperation)) {
            setHasDiscontinuedOperation(data.hasDiscontinuedOperation);
          }

          if (data.conditionAnswers && typeof data.conditionAnswers === "object") {
            const next: Record<string, Record<number, YesNo>> = {};
            for (const [assetId, inner] of Object.entries(data.conditionAnswers)) {
              if (!inner || typeof inner !== "object") continue;
              next[assetId] = {};
              for (const [k, v] of Object.entries(inner)) {
                const idx = Number(k);
                if (!Number.isFinite(idx)) continue;
                if (isYesNo(v)) next[assetId][idx] = v;
              }
            }
            setConditionAnswers(next);
          }

          if (
            data.carryingRowsByAsset &&
            typeof data.carryingRowsByAsset === "object"
          ) {
            const next: Record<string, CarryingComponentRow[]> = {};
            for (const [assetId, rows] of Object.entries(data.carryingRowsByAsset)) {
              if (!Array.isArray(rows) || rows.length === 0) continue;
              const parsed = rows
                .map(parseCarryingRow)
                .filter(Boolean) as CarryingComponentRow[];
              if (parsed.length > 0) next[assetId] = parsed;
            }
            setCarryingRowsByAsset(next);
          }

          if (data.fairValueByAsset && typeof data.fairValueByAsset === "object") {
            const next: Record<string, string> = {};
            for (const [k, v] of Object.entries(data.fairValueByAsset)) {
              if (typeof v === "string") next[k] = v;
            }
            setFairValueByAsset(next);
          }

          if (
            data.costToSellByAsset &&
            typeof data.costToSellByAsset === "object"
          ) {
            const next: Record<string, string> = {};
            for (const [k, v] of Object.entries(data.costToSellByAsset)) {
              if (typeof v === "string") next[k] = v;
            }
            setCostToSellByAsset(next);
          }
        }
      } catch (e) {
        console.warn(`[NCAHS] Failed to load ${FILE_NAME}`, e);
      } finally {
        setLoading(false);
      }
    };
    loadFile();
  }, []);

  const handleBack = () => {
    if (setActiveSection) {
      setActiveSection("ppe-substantive");
      return;
    }
    onBack?.();
  };

  const addAsset = () => setAssets((prev) => [...prev, newAssetRow()]);
  const removeAsset = (id: string) =>
    setAssets((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));

  const updateAsset = (id: string, key: keyof AssetRow, value: string) => {
    setAssets((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)),
    );
  };

  const setConditionAnswer = (
    assetId: string,
    conditionIdx: number,
    value: YesNo,
  ) => {
    setConditionAnswers((prev) => ({
      ...prev,
      [assetId]: {
        ...(prev[assetId] || {}),
        [conditionIdx]: value,
      },
    }));
  };

  const ensureCarryingRows = (assetId: string): CarryingComponentRow[] =>
    carryingRowsByAsset[assetId] || [newCarryingComponentRow()];

  const setCarryingRows = (assetId: string, rows: CarryingComponentRow[]) => {
    setCarryingRowsByAsset((prev) => ({ ...prev, [assetId]: rows }));
  };

  const addCarryingRow = (assetId: string) => {
    setCarryingRows(assetId, [...ensureCarryingRows(assetId), newCarryingComponentRow()]);
  };

  const removeCarryingRow = (assetId: string, rowId: string) => {
    const rows = ensureCarryingRows(assetId);
    if (rows.length === 1) return;
    setCarryingRows(
      assetId,
      rows.filter((r) => r.id !== rowId),
    );
  };

  const updateCarryingRow = (
    assetId: string,
    rowId: string,
    key: keyof CarryingComponentRow,
    value: string,
  ) => {
    setCarryingRows(
      assetId,
      ensureCarryingRows(assetId).map((row) =>
        row.id === rowId ? { ...row, [key]: value } : row,
      ),
    );
  };

  const parseAmount = (raw: string) => {
    const n = Number(String(raw || "").replace(/,/g, "").trim());
    return Number.isFinite(n) ? n : 0;
  };

  const getAmount = (v: number) =>
    new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(v);

  const assetValuation = useMemo(() => {
    const out: Record<
      string,
      { A: number; B: number; C: number; D: number; lower: number }
    > = {};
    assets.forEach((asset) => {
      const A = ensureCarryingRows(asset.id).reduce(
        (sum, r) => sum + parseAmount(r.carryingAmount),
        0,
      );
      const B = parseAmount(fairValueByAsset[asset.id]);
      const C = parseAmount(costToSellByAsset[asset.id]);
      const D = B - C;
      out[asset.id] = { A, B, C, D, lower: Math.min(A, D) };
    });
    return out;
  }, [assets, carryingRowsByAsset, fairValueByAsset, costToSellByAsset]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    setSaveStatus("idle");
    try {
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error(
          "FY_YEAR is not configured. Please ensure you have selected an engagement.",
        );
      }

      const conditionForJson: Record<string, Record<string, YesNo>> = {};
      for (const [assetId, inner] of Object.entries(conditionAnswers)) {
        conditionForJson[assetId] = {};
        for (const [idx, v] of Object.entries(inner)) {
          conditionForJson[assetId][idx] = v;
        }
      }

      const payload: NCAHSPersistedPayload = {
        version: 1,
        lastUpdated: new Date().toISOString(),
        companyName: companyName || undefined,
        hasAssetsHeldForSale,
        assets,
        valuedAtLower,
        hasDiscontinuedOperation,
        conditionAnswers: conditionForJson,
        carryingRowsByAsset,
        fairValueByAsset,
        costToSellByAsset,
      };

      const jsonString = JSON.stringify(payload, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: FILE_NAME,
        referenceValue: "Execution PPE Non-Current Assets Held for Sale",
        folderName: "juggernaut",
        fyYear,
        sectionList: ["Execution_PPE_NCAHS"],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save");
      }

      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (e) {
      console.error("[NCAHS] Save failed", e);
      setSaveStatus("error");
      setSaveError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [
    assets,
    carryingRowsByAsset,
    companyName,
    conditionAnswers,
    costToSellByAsset,
    fairValueByAsset,
    hasAssetsHeldForSale,
    hasDiscontinuedOperation,
    valuedAtLower,
  ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-white/60" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">
            Non-Current Assets Held for Sale
          </h2>
          <p className="text-gray-400">
            Property, Plant & Equipment - Substantive Procedure
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {saveStatus === "saved" && (
            <Badge className="border-green-500/30 bg-green-500/20 text-green-400">
              Saved
            </Badge>
          )}
          {saveStatus === "error" && saveError && (
            <Badge className="max-w-xs truncate border-red-500/30 bg-red-500/20 text-red-400" title={saveError}>
              Error
            </Badge>
          )}
          <Button
            onClick={handleSave}
            disabled={saving || isArchivalMode}
            className="bg-green-600 hover:bg-green-700"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save
              </>
            )}
          </Button>
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      </div>

      {saveError && saveStatus === "error" && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
          {saveError}
        </div>
      )}

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">
            1. Does the entity have non-current assets held for sale or disposal
            group?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-sm">
            <Label className="mb-2 block text-white">Answer</Label>
            <Select
              value={hasAssetsHeldForSale}
              onValueChange={(v) => setHasAssetsHeldForSale(v as YesNo)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Yes / No" />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-black/90 text-white">
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {hasAssetsHeldForSale === "yes" && (
            <div className="space-y-6">
              <Card className="border-white/10 bg-white/5">
                <CardHeader>
                  <CardTitle className="text-white text-base">
                    i. Enter non-current assets held for sale / disposal groups
                    assessed under Ind AS 105
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-white">Category</TableHead>
                        <TableHead className="text-white">Name</TableHead>
                        <TableHead className="text-white">Relationship</TableHead>
                        <TableHead className="text-white">Remarks</TableHead>
                        <TableHead className="w-16 text-white">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assets.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell className="text-white/90">{row.category}</TableCell>
                          <TableCell>
                            <Input
                              value={row.name}
                              onChange={(e) =>
                                updateAsset(row.id, "name", e.target.value)
                              }
                              placeholder="Enter asset/disposal group name"
                              className="border-white/10 bg-black/40 text-white"
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              value={row.relationship}
                              onValueChange={(v) =>
                                updateAsset(row.id, "relationship", v)
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent className="border-white/10 bg-black/90 text-white">
                                {RELATIONSHIP_OPTIONS.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              value={row.remarks}
                              onChange={(e) =>
                                updateAsset(row.id, "remarks", e.target.value)
                              }
                              placeholder="Enter remarks"
                              className="border-white/10 bg-black/40 text-white"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeAsset(row.id)}
                              className="text-red-400 hover:text-red-300"
                              title="Remove row"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Button
                    variant="outline"
                    onClick={addAsset}
                    className="border-white/10"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Row
                  </Button>
                </CardContent>
              </Card>

              {assets
                .filter((a) => a.name.trim().length > 0)
                .map((asset) => (
                  <Card key={asset.id} className="border-white/10 bg-white/5">
                    <CardHeader>
                      <CardTitle className="text-white text-base">
                        Ind AS 105 Conditions - {asset.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <Table
                        className="table-fixed"
                        containerClassName="relative w-full max-w-full overflow-x-hidden"
                      >
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[12%] whitespace-normal text-white align-top">
                              Reference
                            </TableHead>
                            <TableHead className="w-[60%] whitespace-normal text-white align-top">
                              Condition
                            </TableHead>
                            <TableHead className="w-[28%] whitespace-normal text-white align-top">
                              {asset.name}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {CONDITIONS.map((condition, idx) => (
                            <TableRow key={`${asset.id}-${idx}`}>
                              <TableCell className="align-top whitespace-normal break-words text-white/80">
                                {condition.reference || "-"}
                              </TableCell>
                              <TableCell className="align-top whitespace-normal break-words text-white/90 [overflow-wrap:anywhere]">
                                {condition.text}
                              </TableCell>
                              <TableCell className="align-top whitespace-normal">
                                {condition.reference ? (
                                  <Select
                                    value={conditionAnswers[asset.id]?.[idx] || ""}
                                    onValueChange={(v) =>
                                      setConditionAnswer(asset.id, idx, v as YesNo)
                                    }
                                  >
                                    <SelectTrigger className="w-full max-w-full min-w-0">
                                      <SelectValue placeholder="Yes / No" />
                                    </SelectTrigger>
                                    <SelectContent className="border-white/10 bg-black/90 text-white">
                                      <SelectItem value="yes">Yes</SelectItem>
                                      <SelectItem value="no">No</SelectItem>
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <span className="text-white/40">-</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                      <Button variant="outline" className="border-white/10">
                        <Plus className="mr-2 h-4 w-4" />
                        Add
                      </Button>
                    </CardContent>
                  </Card>
                ))}

              <Card className="border-white/10 bg-white/5">
                <CardHeader>
                  <CardTitle className="text-white text-base">
                    ii. Have the assets been valued at the lower of carrying
                    amount and fair value less costs to sell?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="max-w-sm">
                    <Label className="mb-2 block text-white">Answer</Label>
                    <Select
                      value={valuedAtLower}
                      onValueChange={(v) => setValuedAtLower(v as YesNo)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Yes / No" />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-black/90 text-white">
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {assets
                    .filter((a) => a.name.trim().length > 0)
                    .map((asset) => {
                      const rows = ensureCarryingRows(asset.id);
                      const totals = assetValuation[asset.id];
                      return (
                        <Card key={`valuation-${asset.id}`} className="border-white/10 bg-black/30">
                          <CardHeader>
                            <CardTitle className="text-white text-sm">
                              {asset.name}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="text-white">Component</TableHead>
                                  <TableHead className="text-white">
                                    Carrying Amount
                                  </TableHead>
                                  <TableHead className="w-16 text-white">
                                    Action
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {rows.map((row) => (
                                  <TableRow key={row.id}>
                                    <TableCell>
                                      <Select
                                        value={row.component}
                                        onValueChange={(v) =>
                                          updateCarryingRow(asset.id, row.id, "component", v)
                                        }
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder="Execution phase" />
                                        </SelectTrigger>
                                        <SelectContent className="border-white/10 bg-black/90 text-white">
                                          {executionPhases.map((phase) => (
                                            <SelectItem key={phase} value={phase}>
                                              {phase}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </TableCell>
                                    <TableCell>
                                      <Input
                                        value={row.carryingAmount}
                                        onChange={(e) =>
                                          updateCarryingRow(
                                            asset.id,
                                            row.id,
                                            "carryingAmount",
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Enter amount"
                                        className="border-white/10 bg-black/40 text-white"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeCarryingRow(asset.id, row.id)}
                                        className="text-red-400 hover:text-red-300"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            <Button
                              variant="outline"
                              className="border-white/10"
                              onClick={() => addCarryingRow(asset.id)}
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Add Row
                            </Button>

                            <Table
                              className="w-full table-fixed border border-white/10"
                              containerClassName="relative w-full max-w-full overflow-x-hidden"
                            >
                              <TableBody>
                                <TableRow className="border-white/10 hover:bg-transparent">
                                  <TableCell className="w-[40%] align-middle text-white">
                                    Total Carrying Amount (A)
                                  </TableCell>
                                  <TableCell className="w-[60%] align-middle font-semibold text-white">
                                    {getAmount(totals?.A || 0)}
                                  </TableCell>
                                </TableRow>
                                <TableRow className="border-white/10 hover:bg-transparent">
                                  <TableCell className="w-[40%] align-middle text-white">
                                    Fair Value (B)
                                  </TableCell>
                                  <TableCell className="w-[60%] align-middle">
                                    <Input
                                      value={fairValueByAsset[asset.id] || ""}
                                      onChange={(e) =>
                                        setFairValueByAsset((prev) => ({
                                          ...prev,
                                          [asset.id]: e.target.value,
                                        }))
                                      }
                                      placeholder="Enter amount"
                                      className="w-full border-white/10 bg-black/40 text-white"
                                    />
                                  </TableCell>
                                </TableRow>
                                <TableRow className="border-white/10 hover:bg-transparent">
                                  <TableCell className="w-[40%] align-middle text-white">
                                    Less: Costs to Sell (C)
                                  </TableCell>
                                  <TableCell className="w-[60%] align-middle">
                                    <Input
                                      value={costToSellByAsset[asset.id] || ""}
                                      onChange={(e) =>
                                        setCostToSellByAsset((prev) => ({
                                          ...prev,
                                          [asset.id]: e.target.value,
                                        }))
                                      }
                                      placeholder="Enter amount"
                                      className="w-full border-white/10 bg-black/40 text-white"
                                    />
                                  </TableCell>
                                </TableRow>
                                <TableRow className="border-white/10 hover:bg-transparent">
                                  <TableCell className="w-[40%] align-middle text-white">
                                    Net Amount (D = B - C)
                                  </TableCell>
                                  <TableCell className="w-[60%] align-middle font-semibold text-white">
                                    {getAmount(totals?.D || 0)}
                                  </TableCell>
                                </TableRow>
                                <TableRow className="border-white/10 hover:bg-transparent">
                                  <TableCell className="w-[40%] align-middle text-white">
                                    Lower of A and D
                                  </TableCell>
                                  <TableCell className="w-[60%] align-middle font-semibold text-white">
                                    {getAmount(totals?.lower || 0)}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </CardContent>
                        </Card>
                      );
                    })}

                  <Button variant="outline" className="border-white/10">
                    <Plus className="mr-2 h-4 w-4" />
                    Add
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">
            2. Is there any operation discontinued by the entity?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-white/70">
            As per Appendix A to Ind AS 105, a discontinued operation is a component
            of an entity that either has been disposed of or is classified as held
            for sale and: (a) represents a separate major line of business or
            geographical area of operations, (b) is part of a single co-ordinated
            plan to dispose of a separate major line of business or geographical
            area of operations, or (c) is a subsidiary acquired exclusively with a
            view to resale.
          </p>
          <div className="max-w-sm">
            <Label className="mb-2 block text-white">Answer</Label>
            <Select
              value={hasDiscontinuedOperation}
              onValueChange={(v) => setHasDiscontinuedOperation(v as YesNo)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Yes / No" />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-black/90 text-white">
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {hasDiscontinuedOperation === "yes" && (
            <Button variant="outline" className="border-white/10">
              <Plus className="mr-2 h-4 w-4" />
              Add
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
